<script setup lang="ts">
import { Building2, CheckCircle2, ShieldCheck } from "lucide-vue-next";
import LogoMark from "@/components/LogoMark.vue";

defineProps<{ labelledBy: string }>();
</script>

<template>
  <main class="auth-page">
    <section class="auth-story">
      <div class="auth-story__head"><LogoMark /></div>
      <div class="auth-story__content">
        <h1>One working plan.<br />The right view for everyone.</h1>
        <p>
          Coordinate schedules, responsibilities, client decisions, and delivery
          details without splitting the project across spreadsheets and email.
        </p>
        <ul>
          <li>
            <CheckCircle2 :size="18" /> See schedule consequences before
            applying them.
          </li>
          <li>
            <ShieldCheck :size="18" /> Keep project information with the right
            participants.
          </li>
          <li>
            <Building2 :size="18" /> Give every participant a focused,
            accountable view.
          </li>
        </ul>
      </div>
      <div class="auth-story__plan" aria-hidden="true">
        <div class="plan-rule"></div>
        <div class="plan-row"><span></span><span></span><span></span></div>
        <div class="plan-row"><span></span><span></span><span></span></div>
        <div class="plan-bar"></div>
        <div class="plan-milestone"></div>
      </div>
      <p class="auth-story__foot">
        Project Planner · Calm, accountable delivery
      </p>
    </section>
    <section class="auth-panel" :aria-labelledby="labelledBy">
      <slot />
    </section>
  </main>
</template>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(420px, 1.08fr) minmax(430px, 0.92fr);
  background: var(--sheet);
}
.auth-story {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr auto;
  padding: 34px 42px;
  color: white;
  background: var(--ink-900);
}
.auth-story__content {
  position: relative;
  z-index: 2;
  align-self: center;
  max-width: 620px;
  padding-bottom: 100px;
}
.auth-story h1 {
  max-width: 560px;
  font-size: clamp(40px, 5vw, 68px);
  line-height: 0.98;
  letter-spacing: -0.04em;
}
.auth-story__content > p {
  max-width: 570px;
  margin-top: 24px;
  color: #c5d4df;
  font-size: 17px;
  line-height: 1.6;
}
.auth-story ul {
  display: grid;
  gap: 12px;
  margin: 28px 0 0;
  padding: 0;
  list-style: none;
  color: #dce8e2;
}
.auth-story li {
  display: flex;
  align-items: center;
  gap: 10px;
}
.auth-story li svg {
  flex: 0 0 auto;
  color: var(--brand);
}
.auth-story__plan {
  position: absolute;
  right: -80px;
  bottom: 40px;
  width: 64%;
  height: 36%;
  opacity: 0.22;
  transform: rotate(-5deg);
  border: 1px solid #8cb2c9;
  background: var(--ink-850);
}
.plan-rule {
  position: absolute;
  left: 0;
  right: 0;
  top: 24%;
  height: 1px;
  background: #a9c7d8;
}
.plan-row {
  height: 19%;
  margin: 18px 0;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  border-top: 1px solid #8cb2c9;
  border-bottom: 1px solid #8cb2c9;
}
.plan-row span + span {
  border-left: 1px solid #8cb2c9;
}
.plan-bar {
  position: absolute;
  left: 32%;
  top: 47%;
  width: 40%;
  height: 10px;
  background: var(--brand);
}
.plan-milestone {
  position: absolute;
  right: 18%;
  top: 69%;
  width: 14px;
  height: 14px;
  transform: rotate(45deg);
  background: #e2ae5a;
}
.auth-story__foot {
  position: relative;
  z-index: 2;
  color: #a8bdca;
  font-size: 12px;
}
.auth-panel {
  display: grid;
  place-items: center;
  padding: 40px;
}
@media (max-width: 900px) {
  .auth-page {
    grid-template-columns: 1fr;
  }
  .auth-story {
    min-height: 330px;
    padding: 28px;
  }
  .auth-story__content {
    align-self: end;
    padding: 60px 0 20px;
  }
  .auth-story h1 {
    font-size: 38px;
  }
  .auth-story__content > p,
  .auth-story ul {
    display: none;
  }
  .auth-panel {
    min-height: calc(100vh - 330px);
    padding: 34px 22px;
  }
}
@media (max-width: 500px) {
  .auth-story {
    min-height: 260px;
  }
  .auth-story h1 {
    font-size: 32px;
  }
  .auth-panel {
    min-height: calc(100vh - 260px);
  }
}
</style>
